<script setup>
import { ref, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useMemberProfileStore } from '@/stores/memberProfile';
import { useUserStore } from '@/stores/user';
import RadarChart from '@/components/RadarChart.vue';
import { capitalizeWords } from '@/utils/helper';
import { generatePdfRadarImage } from '@/utils/generateRadarImage';

const memberProfileStore = useMemberProfileStore();
const { current } = storeToRefs(useMemberProfileStore());

const userStore = useUserStore();
const { profile } = storeToRefs(userStore);

const imagePreviewUrl = ref(null);
const expandedSummary = ref(true);
const expandedDomains = ref([]);
const expandedRec = ref(true);

const editableSummary = ref('');
const isEditingSummary = ref(false);

const editingDomainHash = ref(null);
const editableDomainText = ref('');

const editableRecommendations = ref('');
const isEditingRecommendations = ref(false);

const isProfileGenerated = computed(() => {
    return Boolean(current.value?.is_generated);
});

const hasCompletedAssessments = computed(() => {
    return current.value?.assessments_completed === true;
});

const startEditingDomain = (domain) => {
    editingDomainHash.value = domain.hash;
    editableDomainText.value = domain.description;
};

const saveDomain = async (domainHash) => {
    await memberProfileStore.updateSingleDomain(
        current.value.hash,
        domainHash,
        { description: editableDomainText.value }
    );

    editingDomainHash.value = null;
};

const startEditingRecommendations = () => {
    editableRecommendations.value = current.value?.generated_recommendations?.recommendation || '';
    isEditingRecommendations.value = true;
};

const saveRecommendations = async () => {
    if (!current.value?.hash) {
        return;
    }

    await memberProfileStore.updateRecommendations(
        current.value.hash,
        { recommendations: editableRecommendations.value }
    );

    isEditingRecommendations.value = false;
};

// Expand all toggled domains by default when data is loaded initially
const expandAllDomains = (domains) => {
    if (Array.isArray(domains) && domains.length > 0) {
        expandedDomains.value = domains.map((d) => d.id);
    } else {
        expandedDomains.value = [];
    }
};

// Toggle one domain open or closed when clicked
const toggleDomain = (id) => {
    if (expandedDomains.value.includes(id)) {
        expandedDomains.value = expandedDomains.value.filter((d) => d !== id);
    } else {
        expandedDomains.value.push(id);
    }
};

const downloadPdf = () => {
    const hash = current.value.hash;

    const rawName = current.value?.member?.user?.full_name || '';
    const formattedName = capitalizeWords(rawName).replace(/\s+/g, '-');

    generatePdfRadarImage(current.value.scores, current.value.domain_map)
        .then((img) => {
            memberProfileStore.downloadPdf(hash, img, formattedName);
        });
};

const downloadAssessment = () => {
    const hash = current.value.hash;
    memberProfileStore.downloadAssessmentExport(hash);
};

const domainDocumentMap = {
    courage: 'courage',
    execution: 'execution',
    identity: 'identity',
    readiness: 'readiness',
};

// Map the domain title to the corresponding document key
const getDomainDocumentKey = (domain) => {
    const title = (domain?.title || '').trim().toLowerCase();

    if (title in domainDocumentMap) {
        return domainDocumentMap[title];
    }

    return null;
};

const canShowDomainDocumentButton = (domain) => {
    return isProfileGenerated.value && Boolean(getDomainDocumentKey(domain));
};

const downloadDomainDocument = (domain) => {
    const hash = current.value?.hash;
    const domainKey = getDomainDocumentKey(domain);

    if (!hash || !domainKey) {
        return;
    }

    memberProfileStore.downloadDomainDocument(hash, domainKey);
};

const isAdmin = computed(() => {
    const user = profile.value;
    return Boolean(user?.is_impersonator_admin);
});

const canEdit = computed(() => {
    const user = profile.value;
    return Boolean(user?.is_accessing_member && user?.is_impersonator_admin);
});

const saveSummary = async () => {
    if (!current.value?.hash) {
        return;
    }

    await memberProfileStore.updateSummaryContent(current.value.hash, {
        general_summary: editableSummary.value,
    });

    isEditingSummary.value = false;
};

watch(
    () => current.value?.generated_summary,
    (summary) => {
        editableSummary.value = summary?.general_summary || '';
    },
    { immediate: true }
);

watch(() => current.value?.generated_domains,
    (domains) => expandAllDomains(domains),
    { immediate: true }
);

watch(
    () => current.value?.generated_recommendations,
    rec => {
        editableRecommendations.value = rec?.recommendation || '';
    },
    { immediate: true }
);

watch(() => memberProfileStore.current?.member, (val) => {
    imagePreviewUrl.value = val?.image_url || null;
});

const recommendationChunks = computed(() => {
    const text = current.value?.generated_recommendations?.recommendation || '';

    // split by blank lines (bullet section separated by an empty line)
    return text
        .trim()
        .split(/\n\s*\n+/)
        .map(s => s.trim())
        .filter(Boolean);
});

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>

    <div v-if="current" class="max-w-4xl mx-auto">
        <!-- Athlete info -->
        <div class="bg-white rounded-3xl shadow-md px-5 py-5 sm:px-6 sm:py-6 mb-8">
            <div class="flex items-start gap-4 sm:gap-10 sm:items-center">
                <div
                    class="flex-shrink-0 flex justify-start self-start sm:self-auto mt-1">
                    <div class="relative">
                        <img
                            v-if="imagePreviewUrl"
                            :src="imagePreviewUrl"
                            class="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover ring-4 ring-indigo-500 shadow-sm">
                        <div
                            v-else
                            class="w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-full ring-4 ring-indigo-500 shadow-sm">
                            <FontAwesomeIcon icon="user" class="text-white text-4xl" />
                        </div>
                    </div>
                </div>

                <div class="flex-1 min-w-0 sm:pl-10 sm:max-w-lg sm:ml-24">
                    <div
                        class="flex items-center justify-start gap-2 sm:gap-3 mb-1 sm:mb-2">
                        <h2
                            class="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight text-left">
                            {{ current.member?.user?.full_name || 'Athlete Name' }}
                        </h2>
                    </div>

                    <div
                        class="flex items-center justify-start gap-3 mb-3 sm:mb-5">
                        <img
                            v-if="current.organization?.logo_url"
                            :src="current.organization.logo_url"
                            class="w-7 h-7 sm:w-10 sm:h-10 object-contain rounded">
                        <p class="text-gray-700 text-sm sm:text-lg font-semibold">
                            {{ current.organization?.name || '' }}
                        </p>
                    </div>

                    <div
                        class="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 max-w-xs mx-0 text-xs sm:text-base">
                        <span class="text-gray-600 font-medium">
                            {{ $t('label.dob') }}:
                        </span>
                        <span class="text-gray-900">
                            {{ current.member?.dob || '—' }}
                        </span>

                        <span class="text-gray-600 font-medium">
                            {{ $t('label.gender') }}:
                        </span>
                        <span class="text-gray-900 capitalize">
                            {{ current.member?.gender || '—' }}
                        </span>

                        <span class="text-gray-600 font-medium">
                            {{ $t('label.sport') }}:
                        </span>
                        <span class="text-gray-900 capitalize">
                            {{ current.sport?.name || '—' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Profile exists but assessments NOT completed -->
        <div
            v-if="current && !hasCompletedAssessments"
            class="bg-white shadow rounded-2xl p-8 mb-8 text-center">
            <p class="text-gray-700 text-lg font-semibold mb-6">
                {{ $t('label.complete_assessments_msg') }}
            </p>

            <StyledButton
                color="primary"
                size="medium"
                class="!px-8 !py-3 !rounded-2xl"
                @click="$inertia.visit('/member-assessments')">
                {{ $t('label.go_to_assessments') }}
            </StyledButton>
        </div>

        <!-- Assessments completed but profile NOT generated yet -->
        <div
            v-else-if="hasCompletedAssessments && !isProfileGenerated"
            class="bg-white shadow rounded-2xl p-6 mb-8 text-center text-gray-600 font-semibold">
            {{ $t('label.profile_generate_wait') }}
        </div>

        <!-- Profile fully generated -->
        <div v-else-if="isProfileGenerated">
            <section class="bg-white shadow rounded-2xl p-6 mb-8">
                <div class="mx-auto max-w-4xl h-[450px]">
                    <RadarChart 
                        v-if="current?.scores"
                        :scores="current.scores"
                        :domain-map="current.domain_map" />
                </div>
            </section>
        
            <!-- General Overview -->
            <section class="bg-white shadow rounded-2xl p-6 mb-8">
                <button
                    class="flex items-center justify-between w-full mb-3"
                    @click="expandedSummary = !expandedSummary">
                    <h3 class="text-lg font-semibold text-indigo-700">
                        {{ $t('label.general_summary') }}
                    </h3>
                    <FontAwesomeIcon
                        :icon="expandedSummary ? 'chevron-up' : 'chevron-down'"
                        class="text-indigo-600 w-4 h-4 transition-transform duration-200" />
                </button>

                <transition name="fade">
                    <div v-show="expandedSummary">
                        <div v-if="canEdit && isEditingSummary">
                            <textarea
                                v-model="editableSummary"
                                rows="6"
                                class="w-full border rounded-lg p-3 text-gray-800" />

                            <div class="flex gap-3 mt-3">
                                <StyledButton
                                    color="primary"
                                    size="small"
                                    @click="saveSummary">
                                    {{ $t('label.save') }}
                                </StyledButton>

                                <StyledButton
                                    color="secondary"
                                    size="small"
                                    @click="isEditingSummary = false">
                                    {{ $t('label.cancel') }}
                                </StyledButton>
                            </div>
                        </div>
                        <div v-else>
                            <p class="text-gray-700 leading-relaxed whitespace-pre-line mb-3">
                                {{ current.generated_summary?.general_summary }}
                            </p>
                            <StyledButton
                                v-if="canEdit"
                                color="primary"
                                size="small"
                                @click="isEditingSummary = true">
                                {{ $t('label.edit') }}
                            </StyledButton>
                        </div>

                    </div>
                </transition>
            </section>

            <!-- Performance Domains -->
            <h3 class="text-lg font-semibold text-indigo-700 mb-3 ml-3">
                {{ $t('label.performance_domains') }}
            </h3>

            <div class="space-y-4 mb-8">
                <div
                    v-for="domain in current.generated_domains"
                    :key="domain.hash"
                    class="bg-white shadow rounded-2xl p-5 hover:shadow-md transition-all w-full text-left">
                    <button
                        class="flex justify-between items-start w-full text-left"
                        @click="toggleDomain(domain.id)">
                        <h4 class="font-semibold text-indigo-700 text-base leading-snug break-words pr-2">
                            {{ domain.title }}
                        </h4>

                        <FontAwesomeIcon
                            :icon="expandedDomains.includes(domain.id) ? 'chevron-up' : 'chevron-down'"
                            class="text-indigo-600 w-4 h-4 transition-transform duration-200" />
                    </button>

                    <transition name="fade">
                        <div v-show="expandedDomains.includes(domain.id)" class="mt-2">
                            <div v-if="canEdit && editingDomainHash === domain.hash">
                                <textarea
                                    v-model="editableDomainText"
                                    rows="5"
                                    class="w-full border rounded-lg p-3" />

                                <div class="flex gap-2 mt-3">
                                    <StyledButton
                                        color="primary"
                                        size="small"
                                        @click="saveDomain(domain.hash)">
                                        {{ $t('label.save') }}
                                    </StyledButton>

                                    <StyledButton
                                        color="secondary"
                                        size="small"
                                        @click="editingDomainHash = null">
                                        {{ $t('label.cancel') }}
                                    </StyledButton>
                                </div>
                            </div>
                            <div v-else>
                                <p class="text-gray-700 leading-relaxed whitespace-pre-line">
                                    {{ domain.description }}
                                </p>

                                <div class="flex flex-wrap gap-3 mt-3">
                                    <StyledButton
                                        v-if="canEdit"
                                        color="primary"
                                        size="small"
                                        @click="startEditingDomain(domain)">
                                        {{ $t('label.edit') }}
                                    </StyledButton>

                                    <StyledButton
                                        v-if="canShowDomainDocumentButton(domain)"
                                        color="primary"
                                        size="small"
                                        @click="downloadDomainDocument(domain)">
                                        {{ $t('label.download_workbook') }}
                                    </StyledButton>
                                </div>
                            </div>

                        </div>
                    </transition>
                </div>
            </div>

            <!-- Focus & Recommendations -->
            <section class="bg-white shadow rounded-2xl p-5 sm:p-6 mb-8 w-full">
                <button
                    class="flex items-center justify-between w-full mb-3"
                    @click="expandedRec = !expandedRec">
                    <h3 class="text-lg font-semibold text-indigo-700">
                        {{ $t('label.focus_and_recommendations') }}
                    </h3>
                    <FontAwesomeIcon
                        :icon="expandedRec ? 'chevron-up' : 'chevron-down'"
                        class="text-indigo-600 w-4 h-4 transition-transform duration-200" />
                </button>

                <transition name="fade">
                    <div v-show="expandedRec" class="text-gray-700 leading-relaxed whitespace-pre-line">
                        <div v-if="canEdit && isEditingRecommendations">
                            <textarea
                                v-model="editableRecommendations"
                                rows="7"
                                class="w-full border rounded-lg p-3 text-gray-800" />

                            <div class="flex gap-3 mt-3">
                                <StyledButton color="primary" size="small" @click="saveRecommendations">
                                    {{ $t('label.save') }}
                                </StyledButton>

                                <StyledButton color="secondary" size="small" @click="isEditingRecommendations = false">
                                    {{ $t('label.cancel') }}
                                </StyledButton>
                            </div>
                        </div>

                        <div v-else>
                            <div class="rec-wrap">
                                <p
                                    v-for="(chunk, idx) in recommendationChunks"
                                    :key="idx"
                                    class="rec-bullet whitespace-pre-line">
                                    {{ chunk }}
                                </p>
                            </div>

                            <StyledButton
                                v-if="canEdit"
                                color="primary"
                                size="small"
                                class="mt-3"
                                @click="startEditingRecommendations">
                                {{ $t('label.edit') }}
                            </StyledButton>
                        </div>

                    </div>
                </transition>
            </section>

            <div class="flex flex-col items-start mt-12 space-y-6">
                <StyledButton
                    v-if="isAdmin"
                    color="primary"
                    size="medium"
                    class="!px-8 !py-3 !rounded-2xl shadow-lg"
                    @click="downloadPdf">
                    <div class="flex items-center gap-3">
                        <FontAwesomeIcon 
                            icon="file-download" 
                            class="w-5 h-5 opacity-90" />

                        <span class="tracking-wide font-semibold">
                            {{ $t('label.download_profile') }}
                        </span>
                    </div>
                </StyledButton>
                <StyledButton
                    v-if="isAdmin"
                    color="primary"
                    size="medium"
                    class="!px-8 !py-3 !rounded-2xl shadow-lg"
                    @click="downloadAssessment">
                    <div class="flex items-center gap-3">
                        <FontAwesomeIcon 
                            icon="file-excel" 
                            class="w-5 h-5 opacity-90" />

                        <span class="tracking-wide font-semibold">
                            {{ $t('label.download_assessment') }}
                        </span>
                    </div>
                </StyledButton>
            </div>
        </div>
    </div>

    <div v-else class="text-center py-12 text-gray-500">
        {{ $t('label.no_profile_found') }}
    </div>

</MainContentLayout>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.rec-wrap {
    margin-top: 6px;
}

.rec-bullet {
    white-space: pre-line;
    padding-left: 18px;
    text-indent: -12px;
    margin-bottom: 14px;
}

</style>
